package io.horion.cv.viewModel

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import io.horion.cv.uiState.LetterUiState
import io.horion.cv.api.datasource.common.PersonalInformation
import io.horion.cv.api.datasource.motivation.Letter
import io.horion.cv.api.datasource.motivation.ModelLetter
import io.horion.cv.api.usecase.CvUseCase
import io.horion.cv.api.utils.LocalStorageManager
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

@Serializable
data class LetterSessionData(
    val data: Letter,
    val modelName: String,
    val enterprise: String,
    val codePostal: String,
    val contact: String,
    val ville: String,
    val addressEmail: String,
    val letterTitle : String
)

class LetterViewModel(
    private val letterUseCase: CvUseCase,
    private val scope: CoroutineScope = CoroutineScope(Dispatchers.Main)
) {
    private val json = Json { ignoreUnknownKeys = true }
    private val SESSION_KEY = "letter_form_data"

    var motivationLetterTitle by mutableStateOf("Lettre de motivation sans titre")
        private set

    private var _letterUiState by mutableStateOf(loadFromSession())
    var letterUiState: LetterUiState
        get() = _letterUiState
        private set(value) {
            _letterUiState = value
            saveToSession()
        }

    var editingPieceJointeId by mutableStateOf<String?>(null)

    var showValidationWarning by mutableStateOf(false)
    var validatedWarning by mutableStateOf(false)
        private set

    var missingFieldIds by mutableStateOf<List<String>>(emptyList())
        private set


    fun updateMotivationLetterTitle(title: String) {
        motivationLetterTitle = title
    }

    fun finishMotivationLetterTitleEdition() {
        motivationLetterTitle = motivationLetterTitle.trim()

        if (motivationLetterTitle.isBlank()) {
            motivationLetterTitle = "Lettre de motivation sans titre"
        }
    }

    private fun loadFromSession(): LetterUiState {
        return try {
            val storage = window.sessionStorage
            val dataJson = storage.getItem(SESSION_KEY)
            if (dataJson != null) {
                val sessionData = json.decodeFromString<LetterSessionData>(dataJson)
                updateMotivationLetterTitle(sessionData.letterTitle)
                LetterUiState(
                    data = sessionData.data,
                    model = try { ModelLetter.valueOf(sessionData.modelName) } catch (_: Exception) { ModelLetter.SIMPLE },
                    enterprise = sessionData.enterprise,
                    codePostal = sessionData.codePostal,
                    contact = sessionData.contact,
                    ville = sessionData.ville,
                    addressEmail = sessionData.addressEmail,
                )
            } else {
                LetterUiState()
            }
        } catch (_: Exception) {
            LetterUiState()
        }
    }

    private fun saveToSession() {
        try {
            val storage = window.sessionStorage
            val sessionData = LetterSessionData(
                data = letterUiState.data,
                modelName = letterUiState.model.name,
                enterprise = letterUiState.enterprise,
                codePostal = letterUiState.codePostal,
                contact = letterUiState.contact,
                ville = letterUiState.ville,
                addressEmail = letterUiState.addressEmail,
                letterTitle = motivationLetterTitle
            )
            storage.setItem(SESSION_KEY, json.encodeToString(sessionData))
        } catch (_: Exception) { }
    }

    fun validateRequiredFields(): Boolean {
        val pi = letterUiState.data.personalInformation
        val missing = mutableListOf<String>()

        if (pi.lastName.isBlank())  missing.add("lastName")
        if (pi.firstName.isBlank()) missing.add("firstName")
        if (pi.address.isBlank())        missing.add("adresse")
        if (pi.email.isBlank())     missing.add("email")
        if (pi.phone.isBlank())     missing.add("phone")
        if (pi.city.isBlank())      missing.add("city")
        if (pi.country.isBlank())      missing.add("country")

        missingFieldIds = missing
        showValidationWarning = missing.isNotEmpty()
        validatedWarning = missing.isEmpty()
        return missing.isEmpty()
    }

    fun dismissValidationWarning() {
        showValidationWarning = false
    }

    fun createLetter() {
        scope.launch {
            letterUiState = letterUiState.copy(
                loading = true,
                letterCreateSuccess = false,
                error = null,
                success = null
            )
            updateDestination()
            try {
                val title = if (motivationLetterTitle == "Lettre de motivation sans titre") "" else motivationLetterTitle
                val response = letterUseCase.createLetter(
                    model = letterUiState.model,
                    letter = letterUiState.data.copy(letterTitle = title)
                )

                when {
                    response == null -> {
                        letterUiState = letterUiState.copy(
                            letterCreateSuccess = false,
                            loading = false,
                            error = "Impossible de contacter le serveur. Vérifiez votre connexion et réessayez."
                        )
                    }
                    response.success -> {
                        LocalStorageManager.saveSessionUuid(response.message)
                        letterUiState = letterUiState.copy(
                            letterCreateSuccess = true,
                            loading = false
                        )
                    }
                    else -> {
                        letterUiState = letterUiState.copy(
                            letterCreateSuccess = false,
                            loading = false,
                            error = response.message
                        )
                    }
                }
            } catch (e: Exception) {
                letterUiState = letterUiState.copy(
                    letterCreateSuccess = false,
                    loading = false,
                    error = e.message
                )
            }
        }
    }


    fun insertLetter(){
        scope.launch {
            letterUiState = letterUiState.copy(
                loading = true,
                error = null,
                success = null
            )
            try {
                val response = letterUseCase.insertLetter(
                    letter = letterUiState.data
                )
                if (response == null){
                    letterUiState = letterUiState.copy(
                        isConnected = false,
                        loading = false
                    )
                } else if (response.success){
                    letterUiState = letterUiState.copy(
                        loading = false,
                        success = response.message
                    )
                }else{
                    letterUiState = letterUiState.copy(
                        loading = false,
                        error = response.message
                    )
                }
            }catch (e : Exception){
                letterUiState = letterUiState.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun clear(){
        letterUiState = letterUiState.copy(
            letterCreateSuccess = false
        )
    }

    fun resetToBlank() {
        letterUiState = LetterUiState(
            data = Letter(personalInformation = PersonalInformation()),
            model = letterUiState.model
        )
    }

    suspend fun loadLetterForEditById(id: String, model: String? = null): Letter? {
        val letter = letterUseCase.getLetterById(id) ?: return null
        val selectedModel = ModelLetter.entries.firstOrNull { it.name == model } ?: ModelLetter.SIMPLE
        letterUiState = letterUiState.copy(data = letter, model = selectedModel)
        return letter
    }

    fun updateExistingLetter(onSuccess: () -> Unit = {}) {
        scope.launch {
            letterUiState = letterUiState.copy(
                loading = true,
                letterCreateSuccess = false,
                error = null,
                success = null
            )
            updateDestination()
            try {
                val response = letterUseCase.updateLetter(
                    letter = letterUiState.data,
                    model = letterUiState.model.name
                )

                when {
                    response == null -> {
                        letterUiState = letterUiState.copy(
                            isConnected = false,
                            loading = false,
                            error = "Impossible de contacter le serveur. Vérifiez votre connexion et réessayez."
                        )
                    }
                    response.success -> {
                        letterUiState = letterUiState.copy(
                            loading = false,
                            success = "Lettre mise à jour"
                        )
                        onSuccess()
                    }
                    else -> {
                        letterUiState = letterUiState.copy(loading = false, error = response.message)
                    }
                }
            } catch (e: Exception) {
                letterUiState = letterUiState.copy(loading = false, error = e.message)
            }
        }
    }
    fun updateModel(model: ModelLetter) {
        letterUiState = letterUiState.copy(
            model = model
        )
    }
    fun updateIntroduction(introductionHtml: String) {
        val currentData = letterUiState.data

        letterUiState = letterUiState.copy(
            data = currentData.copy(introduction = introductionHtml
            )
        )
    }
   fun updateDate(date: String) {
       val currentData = letterUiState.data
       val formattedDate = try {
           // Supposons que la date est au format yyyy-MM-dd
           val parts = date.split("-")
           val year = parts[0]
           val month = parts[1].toInt()
           val day = parts[2].toInt()

           // Map des mois en français
           val months = mapOf(
               1 to "Janvier", 2 to "Février", 3 to "Mars",
               4 to "Avril", 5 to "Mai", 6 to "Juin",
               7 to "Juillet", 8 to "Août", 9 to "Septembre",
               10 to "Octobre", 11 to "Novembre", 12 to "Décembre"
           )

           val monthName = months[month]
           "$day $monthName $year"

       } catch (_: Exception) {
           date
       }
       letterUiState = letterUiState.copy(
           data = currentData.copy(date = formattedDate)
       )

   }

    fun getFormattedDate(): String {

        val date = letterUiState.data.date

        return try {

            val parts = date.split(" ")

            val day = parts[0]
            val monthName = parts[1]
            val year = parts[2]

            val months = mapOf(
                "janvier" to "01",
                "février" to "02",
                "mars" to "03",
                "avril" to "04",
                "mai" to "05",
                "juin" to "06",
                "juillet" to "07",
                "août" to "08",
                "septembre" to "09",
                "octobre" to "10",
                "novembre" to "11",
                "décembre" to "12"
            )

            val monthNumber = months[monthName] ?: "01"

            "$year-$monthNumber-${day.padStart(2, '0')}"

        } catch (_: Exception) {
            ""
        }
    }
    fun updateObjet(objet: String) {
        val currentData = letterUiState.data

        letterUiState = letterUiState.copy(
            data = currentData.copy(subject = objet
            )
        )
    }

    fun updateCodePostal(codePostal: String) {
        letterUiState = letterUiState.copy(
            codePostal = codePostal
        )
    }
    fun updateContact(contact: String) {
        letterUiState = letterUiState.copy(
            contact = contact
        )
    }
    fun updateAdresseEmail(adresseEmail: String) {
        letterUiState = letterUiState.copy(
            addressEmail = adresseEmail
        )
    }
    fun updateVille(ville: String) {
        letterUiState = letterUiState.copy(
            ville = ville
        )
    }
    fun updateSituation(situationHtml: String) {
        val currentData = letterUiState.data

        letterUiState = letterUiState.copy(
            data = currentData.copy(situation = situationHtml
            )
        )
    }

    fun updateMotivation(motivationHtml: String) {
        val currentData = letterUiState.data

        letterUiState = letterUiState.copy(
            data = currentData.copy(motivation = motivationHtml
            )
        )
    }
    fun updateConclusion(conclusionHtml: String) {
        val currentData = letterUiState.data

        letterUiState = letterUiState.copy(
            data = currentData.copy(conclusion = conclusionHtml
            )
        )
    }
    fun updateEnterprise(enterprise: String) {

        letterUiState = letterUiState.copy(
            enterprise = enterprise
        )
    }

    fun updateDestination(){
        val currentData = letterUiState.data
        val destination = "${letterUiState.enterprise},${letterUiState.addressEmail},${letterUiState.ville} ${letterUiState.codePostal}"
        letterUiState = letterUiState.copy(
            data = currentData.copy(
                destination = destination,
                personalInformation = letterUiState.data.personalInformation.copy(
                    sex = "",
                    state = ""
                )
            ),

        )

    }

    fun updatePersonalInfo(update : (PersonalInformation) -> PersonalInformation) {
        val currentData = letterUiState.data

        val currentPersonalInfo =
            currentData.personalInformation

        letterUiState = letterUiState.copy(
            data = currentData.copy(
                personalInformation = update(currentPersonalInfo)
            )
        )
        if (showValidationWarning) {
            validateRequiredFields()
            if (missingFieldIds.isEmpty()) {
                showValidationWarning = false
            }
        }
    }

    fun updatePieceJointe(index: Int, pieceJointes: String) {
        val currentData = letterUiState.data
        val currentList = currentData.others.toMutableList()

        if (index in currentList.indices) {
            currentList[index] = pieceJointes
        } else {
            currentList.add(pieceJointes)
        }

        letterUiState = letterUiState.copy(data = currentData.copy(others = currentList))
    }
    fun deletePieceJointe(index: Int) {
        val currentData = letterUiState.data
        val currentList = currentData.others.toMutableList()

        if (index in currentList.indices) {
            currentList.removeAt(index)
            letterUiState = letterUiState.copy(data = currentData.copy(others = currentList))
        }
    }
    fun clearSection(sectionId: String) {

        when (sectionId) {

            "personal-info" -> {
                updatePersonalInfo { PersonalInformation()}
                /*letterUiState = letterUiState.copy(
                    data = Letter(
                        personalInformation = PersonalInformation()
                    )
                )*/
            }

            "destinataire" -> {
                letterUiState = letterUiState.copy(
                    enterprise = "",
                    contact = "",
                    addressEmail = "",
                    codePostal = "",
                    ville = ""
                )
            }

            "introduction" -> {
                letterUiState = letterUiState.copy(
                    data = letterUiState.data.copy(
                        subject = "",
                        date = "",
                        introduction = ""
                    )
                )
            }

            "situation" -> {
                letterUiState = letterUiState.copy(
                    data = letterUiState.data.copy(
                        situation = ""
                    )
                )
            }

            "motivation" -> {
                letterUiState = letterUiState.copy(
                    data = letterUiState.data.copy(
                        motivation = ""
                    )
                )
            }

            "conclusion" -> {
                letterUiState = letterUiState.copy(
                    data = letterUiState.data.copy(
                        conclusion = ""
                    )
                )
            }

            "piece_jointes" -> {
                /*letterUiState = letterUiState.copy(
                    data = letterUiState.data.copy(
                        others = mutableListOf()
                    )
                )*/
                val currentList = letterUiState.data.others.toMutableList()
                repeat(currentList.size) {
                    deletePieceJointe(0)
                }
            }
        }
    }
}